// brandingColorNames is the single source of truth for the theme colors.  The BrandingColors type is derived from it,
// and it is iterated at runtime to generate the CSS variables (<Branding />) and the Tailwind colors
// (tailwind.config.ts), so adding a name here adds it everywhere (and every theme must then define it).
//
// The on* colors are the text colors that should be used on top of the matching color.  They should have enough
// contrast to be readable.
//
// Each color is exposed as a Tailwind color of the same name in kebab-case (e.g. mutedForeground -> muted-foreground,
// onPrimary -> on-primary).
export const brandingColorNames = [
  // The primary color is the main color of the brand.
  'primary',
  'onPrimary',

  // The secondary color is used for secondary actions or elements.
  'secondary',
  'onSecondary',

  // The destructive color is used for actions that are destructive (e.g. deleting something).
  'destructive',
  'onDestructive',

  // The accent color is used for hover and selected states (e.g. table rows, menu items).
  'accent',
  'onAccent',

  // The widget color is used for surfaces that sit on the background (e.g. cards, inputs, dialogs, menus).
  'widget',
  'onWidget',

  // The background color is used for the background of the application.
  'background',
  'onBackground',

  // heading is the color of page, section, and card titles.
  'heading',

  // muted is a subtle fill (e.g. table headers, empty states) and mutedForeground is the color of secondary text.
  'muted',
  'mutedForeground',

  // border is the default hairline color, input is the border of form controls, and ring is the focus ring.
  'border',
  'input',
  'ring',

  // link is the color of inline text links.
  'link',

  // success and warning are status colors (e.g. badges, status icons).
  'success',
  'onSuccess',
  'warning',
  'onWarning',

  // nav is the background of the app navigation (e.g. the vportal side nav).
  'nav',
  'onNav'
] as const;

// BrandingColorName is one of the theme color names (e.g. 'primary').
export type BrandingColorName = (typeof brandingColorNames)[number];

// BrandingColors is a theme: a hex color for every name in brandingColorNames.
export type BrandingColors = Record<BrandingColorName, string>;

// brandingColorKebab converts a color name to the kebab-case name used for the Tailwind color and CSS variable
// (e.g. mutedForeground -> muted-foreground).
export const brandingColorKebab = (name: BrandingColorName): string =>
  name.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`);

// brandingColorVar returns the name of the CSS variable that holds the color as space separated RGB channels (e.g.
// "40 120 253").  Channels (instead of a hex value) are what allow Tailwind opacity modifiers like bg-primary/10.
export const brandingColorVar = (name: BrandingColorName): string => `--color-${brandingColorKebab(name)}`;
